import React from "react";
import PropTypes from "prop-types";
import { v4 as uuid } from "uuid";

import { IInputProps, IInputPropsCheck } from "../interfaces/IForm";

const defaultInputProps = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  extraClass: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function getError(error, errorName: string | null = null) {
  if (!error) return null;

  let responseError;

  if (typeof error === "string") {
    responseError = error;
  } else if (
    errorName &&
    typeof error === "object" &&
    Object.keys(error).length > 0
  ) {
    if (typeof error[errorName] !== "undefined") {
      responseError = error[errorName];
    }
  } else if (Array.isArray(error)) {
    responseError = error.find((e) => typeof e !== "undefined");
  }

  return responseError;
}
function InputField(props: IInputProps) {
  const {
    label,
    id,
    name,
    extraClass,
    type,
    value,
    placeholder,
    onChange,
    onKeyUp,
    error,
    disabled,
  } = props;
  const errorValue = getError(error, name || id);

  const inputLabel = label ? <label htmlFor={id}>{label}</label> : null;
  let errorLabel;

  if (errorValue) {
    errorLabel = <span className="input-label-error">{errorValue}</span>;
  }

  return (
    <div className={`form-group ${extraClass || ""}`}>
      {inputLabel}
      <input
        type={type || "text"}
        className="form-control"
        id={id}
        name={name || id}
        placeholder={placeholder || ""}
        value={value}
        onChange={onChange}
        disabled={disabled}
        onKeyUp={onKeyUp}
      />
      {errorLabel}
    </div>
  );
}

InputField.propTypes = {
  ...defaultInputProps,
};

function MultipleSelect({ label, id, error, disabled, options }) {
  const errorValue = getError(error, id);

  let errorLabel;
  if (errorValue) {
    errorLabel = <span className="input-label-error">{errorValue}</span>;
  }
  return (
    <div className={`input-select-multiple ${disabled ? "disabled" : ""}`}>
      <span className="input-select-multiple label">{label}</span>
      <div>
        <select
          className="select2-multi-select form-control"
          id={id}
          name={id}
          multiple={true}
        >
          {options.map((o) => (
            <option key={uuid()} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {errorLabel}
    </div>
  );
}

MultipleSelect.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  disabled: PropTypes.bool,
};

function TextArea(props: IInputProps) {
  const {
    label,
    id,
    name,
    extraClass,
    value,
    placeholder,
    onChange,
    error,
    disabled,
  } = props;
  const errorValue = getError(error, name || id);

  const inputLabel = label ? <label htmlFor={id}>{label}</label> : null;
  let errorLabel;

  if (errorValue) {
    errorLabel = <span className="input-label-error">{errorValue}</span>;
  }

  return (
    <div className={`form-group ${extraClass || ""}`}>
      {inputLabel}
      <textarea
        className="form-control"
        id={id}
        name={name}
        placeholder={placeholder || ""}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {errorLabel}
    </div>
  );
}

TextArea.propTypes = {
  ...defaultInputProps,
};

function InputSelect({
  label,
  id,
  name,
  extraClass,
  value,
  onChange,
  error,
  disabled,
  options,
  addNoneOption,
}) {
  const errorValue = getError(error, name || id);

  const inputLabel = label ? <label htmlFor={id}>{label}</label> : null;
  let errorLabel;
  let noneOption;
  let optionsContent;

  if (errorValue) {
    errorLabel = <span className="input-label-error">{errorValue}</span>;
  }

  if (addNoneOption) {
    noneOption = <option value="0">Ninguno</option>;
  }

  optionsContent = (
    <>
      {noneOption}
      {options.map((o) => (
        <option key={uuid()} value={o.value}>
          {o.label}
        </option>
      ))}
    </>
  );

  return (
    <div className={`form-group ${extraClass || ""}`}>
      {inputLabel}
      <select
        className="form-control"
        name={name || id}
        id={id}
        onChange={onChange}
        value={value}
        disabled={disabled}
      >
        {optionsContent}
      </select>
      {errorLabel}
    </div>
  );
}

InputSelect.propTypes = {
  ...defaultInputProps,
  name: PropTypes.string,
  options: PropTypes.array.isRequired,
  addNoneOption: PropTypes.bool.isRequired,
};
InputSelect.defaultProps = {
  addNoneOption: true,
};

function InputSelectSearch({
  label,
  id,
  extraClass,
  value,
  buttonClass,
  error,
  options,
  addNoneOption,
}) {
  const errorValue = getError(error, id);

  const inputLabel = label ? (
    <label htmlFor={id} className="hierarchy-select-label">
      {label}
    </label>
  ) : null;
  let errorLabel;
  let noneOption;
  let optionsContent;

  if (errorValue) {
    errorLabel = <span className="input-label-error">{errorValue}</span>;
  }

  if (addNoneOption) {
    noneOption = (
      <a href="#!" className="dropdown-item" data-value="0">
        Ninguno
      </a>
    );
  }

  optionsContent = (
    <>
      {noneOption}
      {options.map((o) => (
        <a
          href="#!"
          key={uuid()}
          className="dropdown-item"
          data-value={o.value}
        >
          {o.label}
        </a>
      ))}
    </>
  );

  return (
    <div
      className={`dropdown hierarchy-select hierarchy-select-custom ${
        extraClass || ""
      }`}
      id={id}
    >
      {inputLabel}
      <button
        type="button"
        className={`btn ${buttonClass} dropdown-toggle`}
        id={`ssd_${id}`}
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      ></button>
      <div className="dropdown-menu" aria-labelledby={`ssd_${id}`}>
        <div className="hs-searchbox">
          <input type="text" className="form-control" />
        </div>
        <div className="hs-menu-inner">{optionsContent}</div>
      </div>
      {errorLabel}
      <input
        className="d-none"
        name="example_two"
        aria-hidden="true"
        type="text"
        readOnly={true}
      />
    </div>
  );
}

InputSelectSearch.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  extraClass: PropTypes.string,
  buttonClass: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  options: PropTypes.array.isRequired,
  addNoneOption: PropTypes.bool.isRequired,
};
InputSelectSearch.defaultProps = {
  addNoneOption: true,
  buttonClass: "",
};

function CheckField(props: IInputPropsCheck) {
  const { label, id, name, extraClass, onChange, error, disabled, checked } =
    props;
  const errorValue = getError(error, name || id);
  let errorLabel;

  if (errorValue) {
    errorLabel = <span className="input-label-error">{errorValue}</span>;
  }

  return (
    <div className={`custom-control custom-checkbox ${extraClass || ""}`}>
      <input
        id={id}
        name={name || id}
        type="checkbox"
        className="custom-control-input"
        onChange={onChange}
        checked={checked}
        disabled={disabled}
      />
      <label className="custom-control-label" htmlFor={id}>
        {label}
      </label>
      {errorLabel}
    </div>
  );
}

CheckField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  extraClass: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  checked: PropTypes.bool.isRequired,
};

export {
  InputField,
  MultipleSelect,
  TextArea,
  InputSelect,
  InputSelectSearch,
  CheckField,
};
